import type { LoginRequest } from '@avrahami/contracts';
import {
  Centered,
  Loading,
  MFA_PENDING,
  safeRedirect,
  SIGNED_OUT,
  signedIn,
  SignInScreen,
} from '@avrahami/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { sessionApi, sessionChanged, sessionQuery } from '../api/session';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';

/** Password first; when the account has a second factor, the code step follows. */
export function LoginPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const target = safeRedirect(useSearch({ from: '/login' }).redirect, '/tenants');
  const session = useQuery(sessionQuery);

  // Already signed in (another tab, or right after the second step): go where the user was headed.
  const isSignedIn = session.data?.kind === 'signed-in';
  useEffect(() => {
    if (isSignedIn) void navigate({ href: target });
  }, [isSignedIn, navigate, target]);

  if (session.isPending || isSignedIn) {
    return (
      <Centered product={he.app.product}>
        <Loading />
      </Centered>
    );
  }

  // Nothing cached for a previous user survives a new sign-in.
  const login = async (body: LoginRequest) => {
    const result = await sessionApi.login(body);
    sessionChanged(
      queryClient,
      result.status === 'MFA_REQUIRED' ? MFA_PENDING : signedIn(result.me),
    );
    return result;
  };
  const verify = async (code: string) => {
    sessionChanged(queryClient, signedIn(await sessionApi.verifyMfa(code)));
  };
  const startOver = async () => {
    try {
      await sessionApi.logout();
    } finally {
      sessionChanged(queryClient, SIGNED_OUT);
    }
  };

  return (
    <SignInScreen
      product={he.app.product}
      words={he.login}
      devCodeWords={he.devCode}
      mfaPending={session.data?.kind === 'mfa-pending'}
      login={login}
      verify={verify}
      startOver={startOver}
      errorText={errorMessage}
      forgotLink={
        <Link to="/forgot-password" className="text-center text-sm text-brand-700 underline">
          {he.forgotPassword.link}
        </Link>
      }
    />
  );
}
